import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

// Redux
import { openModal, closeModal } from '../state/actions/ui/modal';
import { getCurrentModal } from '../state/selectors/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';

// Utils
import { compressForUpload } from '../utils/imageCompression';
import {
  saveFilesIntoADirectory,
  deleteFilesFromDirectory,
  deleteWholeDirectory,
} from '../utils/files';
import {
  enqueuePhotosForUpload,
  entityTypeForOrderType,
} from '../utils/enqueuePhotos';
import { getImageUploadQueue } from '../offline/offlineImageUploadQueue';

// Types
import { OrderType } from '../types/orders';
import type { ImageData } from '../components/inputs/InputLoadImages/types';

export interface SegmentPhotosTarget {
  /** Id de la ENTIDAD RouteSegment (no el de la Order). */
  segmentId: string;
  /** Order gemela del tramo — es a ella a la que cuelgan las imágenes. */
  orderId: string;
  indexExpedition: number;
  indexType: number;
  /** Fotos que el tramo YA tiene guardadas (para no repetir numeración). */
  existingPhotos?: number;
}

/** Un directorio por tramo: dos tramos seguidos no se pisan los blobs. */
const workingDirFor = (segmentId: string) => `segment-photos-${segmentId}`;

/**
 * Cadena de fotos de un tramo: capturar, ver, quitar y encolar.
 *
 * ─── Por qué es un hook y no código en cada llamador ───────────────────────
 *
 * Hay DOS puntos de entrada a la misma cadena —el cierre del tramo
 * (`TimerOverlay`) y la card del tramo (`RouteSegmentOT`)— y la lógica de
 * encolado es justo la que no debe existir por duplicado. Es la misma razón
 * por la que `canEditRouteSegments` vive en `utils/surveillanceRoute`.
 *
 * ─── Por qué el estado vive AQUÍ y no dentro del modal ─────────────────────
 *
 * El sistema de modales de la app es de uno en uno: `openModal` REEMPLAZA el
 * modal actual. Pasar por la cámara desmonta el modal de multimedia, así que
 * si la lista de fotos viviera dentro se perdería en cada disparo.
 *
 * ─── Por qué se encola en un lote y no foto a foto ─────────────────────────
 *
 * Es el mismo momento de «guardar» que usa el asistente: un lote, un evento,
 * una actualización del banner. Además, quitar una foto antes de ese momento
 * es sacarla de una lista — no hay que deshacer una subida ya hecha.
 *
 * ─── Añadir SOLO con el botón «Añadir»; cerrar DESCARTA ────────────────────
 *
 * La X, Escape y el click fuera son gestos de «déjalo como estaba», no de
 * «guarda». Antes el cierre encolaba, así que asomarse al modal y salir subía
 * fotos que nadie pidió subir. Ahora el único camino que encola es el botón, y
 * cerrar borra el directorio de trabajo con lo capturado.
 */
export function useSegmentPhotos(): {
  openPhotoChain: (target: SegmentPhotosTarget) => void;
} {
  const dispatch = useDispatch();
  /**
   * El fin de la cadena se detecta por el ESTADO del modal, no por un callback
   * de cierre.
   *
   * 🔴 Razón (cazada por el E2E): `BaseModal` atiende Escape y el click fuera
   * llamando a su PROPIO cierre, que despacha `closeModal()` directamente y se
   * salta el `onClickClose` que SuperModal pasa al submodal. Colgar el encolado
   * de ese callback significaba perder las fotos en silencio en cuanto el
   * operario cerrase con Escape o tocando fuera — justo los dos gestos más
   * naturales en una tablet.
   */
  // Se selecciona el ID (un string), NO el objeto del modal: con el objeto,
  // `useSelector` re-renderiza a CADA acción de modal de toda la app, y este
  // hook vive en `TimerOverlay` —global y siempre montado— y en cada card de
  // tramo. Con un primitivo, react-redux corta el re-render cuando no cambia.
  const currentModalId = useSelector(
    (state: unknown) =>
      (getCurrentModal(state) as { id: string | null } | undefined)?.id ?? null
  );

  const [photos, setPhotos] = useState<ImageData[]>([]);
  const targetRef = useRef<SegmentPhotosTarget | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Evita reabrir el modal en el primer render y una vez cerrada la cadena. */
  const chainOpenRef = useRef(false);
  /**
   * `true` mientras la cadena sale a propósito del modal de multimedia (a la
   * cámara o al selector de ficheros). Sin esta marca, ese hueco sin modal se
   * leería como «el operario ha cerrado» y encolaría a medias.
   */
  const returningRef = useRef(false);
  /**
   * Espejo de `photos`. Los callbacks viajan a Redux dentro del payload del
   * modal, así que se congelan con el valor que tenían al abrirlo; sin el ref
   * leerían una lista vieja.
   */
  const photosRef = useRef<ImageData[]>([]);
  /**
   * Ref a la última versión de `openMediaModal`: el `onClose` de la cámara se
   * define ANTES que ella y no puede capturarla directamente.
   */
  const openMediaModalRef = useRef<(items: ImageData[]) => void>(
    () => undefined
  );

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  /**
   * Entradas de fotos pendientes, EN FILA. «Añadir» la espera antes de mirar
   * qué encolar: con «Hecho» la cámara entrega todas las fotos de golpe y
   * cierra, y si «Añadir» se pulsaba antes de que entraran, se encolaba la
   * lista vieja y las nuevas se perdían sin aviso.
   */
  const addingRef = useRef<Promise<void>>(Promise.resolve());

  const addFilesNow = useCallback(async (
    files: File[],
    { alreadyCompressed = false }: { alreadyCompressed?: boolean },
    target: SegmentPhotosTarget | null
  ) => {
    // Solo para la cadena en la que se pidió: en la fila puede esperar, y si
    // entretanto se cerró esa cadena y se abrió otra, estas fotos no son suyas.
    const stillSameChain = () => !!target && targetRef.current === target;
    if (!stillSameChain() || !target || files.length === 0) return;

    try {
      // El descarte de duplicados va ANTES de comprimir, igual que en el
      // formulario de actuación: al revés se comprime la foto y se tira el
      // resultado, y comprimiendo siempre eso son ~400 ms de worker tirados
      // por cada foto ya añadida. Elegir dos veces el mismo fichero de la
      // galería es lo normal, no un caso raro.
      const alreadyAdded = new Set(photosRef.current.map((p) => p.id));
      const incoming = files.filter((f) => !alreadyAdded.has(f.name));
      if (incoming.length === 0) return;

      // Misma compresión que el resto de la app (2560 px / 1,2 MB): una foto
      // de tablet moderna pesa 10 MB y en carretera eso son minutos de subida.
      // 🔴 De una en una, nunca con `Promise.all`: cada compresión descodifica
      // la foto entera (~20 MB) y varias a la vez tumban el WebView
      // (`.claude/rules/webview-memoria-apk.md`). Las de la cámara ya vienen
      // comprimidas y con su EXIF: recomprimirlas solo gastaría memoria.
      const prepared: File[] = [];
      for (const file of incoming) {
        prepared.push(alreadyCompressed ? file : await compressForUpload(file));
      }

      if (!stillSameChain()) return;
      await saveFilesIntoADirectory(workingDirFor(target.segmentId), prepared);
      if (!stillSameChain()) return;

      const added = prepared.map((file) => ({
        id: file.name,
        name: file.name,
        date: new Date(file.lastModified || Date.now()).toISOString(),
        previewUrl: URL.createObjectURL(file),
      }));
      // La ref al momento: la siguiente entrada de la fila y «Añadir» la leen
      // antes de que React vuelva a pintar.
      photosRef.current = [...photosRef.current, ...added];
      setPhotos((prev) => [...prev, ...added]);
    } catch (err) {
      // Que una foto no se pueda guardar en disco no puede tumbar la cadena:
      // el operario sigue pudiendo hacer otra o cerrar sin ninguna.
      Sentry.captureException(err);
    }
  }, []);

  const addFiles = useCallback(
    (files: File[], options: { alreadyCompressed?: boolean } = {}) => {
      const target = targetRef.current;
      const run = () => addFilesNow(files, options, target);
      const job = addingRef.current.then(run, run);
      addingRef.current = job.catch(() => undefined);
      return job;
    },
    [addFilesNow]
  );

  /**
   * Input de fichero oculto para «Escollir de la galeria», creado BAJO DEMANDA.
   *
   * Antes se creaba en el montaje del hook, y como el hook vive en el
   * `TimerOverlay` global y en CADA card de tramo, un recorrido con treinta
   * tramos metía treinta inputs y treinta listeners en el `body` aunque nadie
   * abriera la cadena. Ahora existe como mucho uno, el de la cadena activa, y
   * se retira al terminar la cadena (confirmada o descartada).
   */
  const ensureFileInput = useCallback((): HTMLInputElement => {
    if (inputRef.current) return inputRef.current;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.style.display = 'none';
    input.setAttribute('data-testid', 'segment-photos-file-input');
    input.addEventListener('change', () => {
      const files = Array.from(input.files ?? []);
      input.value = '';
      void addFiles(files);
      // Volver al modal aunque no se haya elegido nada (cancelar el selector):
      // sin esto el operario se queda fuera de la cadena.
      if (chainOpenRef.current) {
        openMediaModalRef.current(photosRef.current);
      }
    });
    document.body.appendChild(input);
    inputRef.current = input;
    return input;
  }, [addFiles]);

  // Red de seguridad: si el componente se desmonta con la cadena a medias, el
  // input no puede quedarse colgado del `body`.
  useEffect(
    () => () => {
      inputRef.current?.remove();
      inputRef.current = null;
    },
    []
  );

  const removePhoto = useCallback((id: string) => {
    const target = targetRef.current;
    setPhotos((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      if (target) {
        void deleteFilesFromDirectory(workingDirFor(target.segmentId), id).catch(
          (err) => Sentry.captureException(err)
        );
      }
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  /** Suelta lo capturado y cierra la cadena. NO sube nada. */
  const teardownChain = useCallback(async (deleteWorkingDir: boolean) => {
    const target = targetRef.current;
    const captured = photosRef.current;

    if (target && deleteWorkingDir) {
      // Lo capturado y descartado no puede quedarse en OPFS: son megas por
      // foto y nadie volverá a mirar ese directorio.
      await deleteWholeDirectory(workingDirFor(target.segmentId)).catch(
        (err) => Sentry.captureException(err)
      );
    }
    captured.forEach((p) => {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
    // El input oculto solo vive mientras dura la cadena.
    inputRef.current?.remove();
    inputRef.current = null;
    setPhotos([]);
    photosRef.current = [];
    targetRef.current = null;
  }, []);

  /**
   * Cerrar sin confirmar: se descarta lo capturado.
   *
   * Es el camino de la X, Escape y el click fuera — los tres pasan por
   * `BaseModal`, que despacha su propio `closeModal()` y se salta el
   * `onClickClose` del submodal, así que el fin de cadena se detecta por el
   * ESTADO del modal (ver el efecto de más abajo) y no por un callback.
   */
  const discardChain = useCallback(async () => {
    chainOpenRef.current = false;
    await teardownChain(true);
  }, [teardownChain]);

  /** Botón «Añadir»: encola el lote. Único camino que sube algo. */
  const confirmChain = useCallback(async () => {
    const target = targetRef.current;
    chainOpenRef.current = false;
    if (!target) return;

    // Lo que la cámara acaba de entregar puede estar aún entrando.
    await addingRef.current;
    const captured = photosRef.current;
    if (captured.length > 0) {
      try {
        // 🔴 Desde dónde numerar. La cola deduplica por
        // `(orderId, kind, photoSequence)` y BORRA las `pending` que coincidan,
        // así que una segunda tanda numerada desde 1 se lleva por delante la
        // primera —con sus blobs y sin avisar—. Y esta cadena se abre DOS
        // veces sobre el mismo tramo: al cerrarlo y luego desde su card.
        //
        // No basta con contar las guardadas: hasta que la tanda anterior no
        // drena, el servidor no las conoce. Se toma el mayor de las dos
        // fuentes: lo guardado y lo que aún espera en la cola.
        let queuedMax = 0;
        try {
          queuedMax = (await getImageUploadQueue())
            .filter((o) => o.kind === 'photo' && o.orderId === target.orderId)
            .reduce((max, o) => Math.max(max, o.photoSequence ?? 0), 0);
        } catch (err) {
          Sentry.captureException(err);
        }
        const fromSequence = Math.max(target.existingPhotos ?? 0, queuedMax);

        await enqueuePhotosForUpload({
          picturesDirectoryName: workingDirFor(target.segmentId),
          orderId: target.orderId,
          orderType: OrderType.RouteSegment,
          entityType: entityTypeForOrderType(OrderType.RouteSegment),
          // Solo lo capturado en ESTA cadena. El directorio de trabajo se borra
          // al terminar, así que normalmente no hay nada más — pero si una
          // cadena anterior murió a medias (recarga, cierre de la app) sus
          // ficheros siguen ahí, y enumerar el directorio entero los re-subiría
          // creando un `Image` duplicado por cada uno. Es el mismo fallo que
          // destapó `onlyNames` en el formulario de actuación.
          onlyNames: captured.map((p) => p.name),
          fromSequence,
          orderMetadata: {
            indexExpedition: target.indexExpedition,
            indexType: target.indexType,
            // El ROID del tramo es `Tr.<indexExpedition>.<indexType>.02`.
            classification: '02',
            orderType: OrderType.RouteSegment,
          },
        });
        // El directorio de trabajo ya no hace falta: `enqueuePhotosForUpload`
        // copia los blobs al directorio de pendientes, que es el que sobrevive
        // a las limpiezas.
        await deleteWholeDirectory(workingDirFor(target.segmentId));
      } catch (err) {
        Sentry.captureException(err);
      }
    }

    // El directorio ya lo borra el `try` de arriba tras encolar; volver a
    // pedirlo aquí solo añadiría un fallo esperado en el log.
    await teardownChain(false);
  }, [teardownChain]);

  const openMediaModal = useCallback(
    (current: ImageData[]) => {
      dispatch(
        openModal(modalIds.ADD_MULTIMEDIA_MODAL, {
          images: current,
          onRemoveImage: removePhoto,
          // Botón «Añadir»: el ÚNICO camino que encola. `confirmChain` pone
          // `chainOpenRef` a false antes de su primer `await`, así que el
          // `closeModal()` de la línea siguiente ya no dispara el descarte.
          onConfirm: () => {
            void confirmChain();
            dispatch(closeModal());
          },
          handleOpenCamera: () => {
            // Salida deliberada: el hueco sin modal que viene ahora no es un
            // cierre del operario.
            returningRef.current = true;
            dispatch(
              openModal(modalIds.OPEN_CAMERA_MODAL, {
                // `OpenCameraModal` empuja la foto a estos dos setters y se
                // cierra solo. Solo interesa `setFilesState`, que es el que
                // trae el `File`; `setImagesState` recibe metadatos que aquí se
                // recalculan (con la miniatura) en `addFiles`.
                setImagesState: () => undefined,
                setFilesState: (
                  updater:
                    | { id: string; file: File }[]
                    | ((
                        prev: { id: string; file: File }[]
                      ) => { id: string; file: File }[])
                ) => {
                  const next =
                    typeof updater === 'function' ? updater([]) : updater;
                  void addFiles(
                    next.map((entry) => entry.file),
                    { alreadyCompressed: true }
                  );
                },
                type: String(OrderType.RouteSegment).toUpperCase(),
                // El tramo no tiene formulario en sessionStorage del que sacar
                // la carretera para el nombre temporal; el nombre definitivo lo
                // pone `generateImageNameWithROID` al encolar.
                dataBaseNamingCaching: undefined,
              })
            );
          },
          handleChoosePick: () => {
            // Igual que la cámara: salida deliberada, no un cierre.
            returningRef.current = true;
            dispatch(closeModal());
            ensureFileInput().click();
          },
        })
      );
    },
    [dispatch, removePhoto, confirmChain, ensureFileInput]
  );

  useEffect(() => {
    openMediaModalRef.current = openMediaModal;
  }, [openMediaModal]);

  // Refrescar la pila del modal cuando entra una foto nueva.
  useEffect(() => {
    if (!chainOpenRef.current || !targetRef.current || photos.length === 0) {
      return;
    }
    // 🔴 Con la cámara abierta, NO. El sistema de modales tiene una sola ranura
    // y `openModal` reemplaza, así que reabrir el de multimedia aquí mataría el
    // visor en mitad de una ráfaga: la primera foto aceptada cerraría la cámara.
    // Al volver del visor, el efecto de fin de cadena ya reabre la pila con la
    // lista completa. Mismo guard que usa ese efecto unas líneas más abajo.
    if (currentModalId === modalIds.OPEN_CAMERA_MODAL) return;
    // Se llama por el REF a propósito: `openMediaModal` cambia en cada render
    // por sus dependencias, y ponerlo en el array de abajo provocaría un bucle
    // de reapertura. Solo interesa reaccionar a la LISTA.
    openMediaModalRef.current(photos);
  }, [photos]);

  /**
   * Fin de la cadena, o vuelta al modal de multimedia.
   *
   * Que no haya ningún modal en pantalla significa una de dos cosas: o venimos
   * de una salida deliberada (cámara / selector de ficheros) y hay que volver,
   * o el operario ha cerrado — y entonces es el momento de encolar el lote.
   * Mirar el ESTADO en vez de un callback cubre de una vez la X, Escape y el
   * click fuera, que son tres caminos distintos dentro de `BaseModal`.
   */
  useEffect(() => {
    if (!chainOpenRef.current) return;
    if (currentModalId === modalIds.ADD_MULTIMEDIA_MODAL) {
      // De vuelta en casa: la salida deliberada ya se consumió. Sin este
      // reseteo, el siguiente cierre —el de verdad— se leería como otra vuelta
      // y el lote no se encolaría nunca.
      returningRef.current = false;
      return;
    }
    if (currentModalId === modalIds.OPEN_CAMERA_MODAL) return;
    if (returningRef.current) {
      returningRef.current = false;
      openMediaModalRef.current(photosRef.current);
      return;
    }
    void discardChain();
  }, [currentModalId, discardChain]);

  const openPhotoChain = useCallback(
    (target: SegmentPhotosTarget) => {
      targetRef.current = target;
      chainOpenRef.current = true;
      setPhotos([]);
      photosRef.current = [];
      // Empezar en blanco, como el resto de la app tras el bug del borrador
      // del RV: si una cadena anterior murió a medias (recarga, cerrar la app)
      // sus ficheros siguen en el directorio de trabajo. Encolar ya está
      // protegido por `onlyNames`, pero sin esta purga los huérfanos se
      // acumulan en OPFS para siempre. Lo YA encolado no corre peligro: vive
      // en el directorio de pendientes, no en este.
      void deleteWholeDirectory(workingDirFor(target.segmentId)).catch(() => {
        // No existe todavía: es el caso normal la primera vez.
      });
      openMediaModal([]);
    },
    [openMediaModal]
  );

  return { openPhotoChain };
}

export default useSegmentPhotos;
